<template>
  <Dashboard :title="$t('upgrade.claim_trial.layout_title')">
    <div
      v-if="loading"
      class="w-full px-4 lg:px-16 flex flex-col items-center justify-center py-24"
    >
      <p class="text-text-secondary">{{ $t('common.loading') }}</p>
    </div>
    <div
      v-else-if="campaign"
      class="w-full px-4 lg:px-16 flex flex-col items-center gap-3"
    >
      <TrialOfferCard
        :campaign="campaign"
        :tier-label="tierLabel"
        :is-player-tier="isPlayerTier"
        :tier="tier"
        :submitting="submitting"
        @submit="continueAndSubmit"
      />
    </div>
    <AlertPopup />
  </Dashboard>
</template>

<script setup>
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { onMounted, ref, computed } from 'vue';
import { useAuth } from '@websanova/vue-auth/src/v3.js';
import { useGtm } from '@gtm-support/vue-gtm';
import { useStore } from 'vuex';
import { useHead } from '@unhead/vue';
import TrialOfferCard from '../components/TrialOfferCard.vue';
import AlertPopup from '../components/AlertPopup.vue';
import Dashboard from '../components/layouts/DashboardLayout.vue';
import { useTrialCampaign } from '../composables/useTrialCampaign';

const { t } = useI18n();

const gtm = useGtm();
const router = useRouter();
const route = useRoute();
const auth = useAuth();
const user = ref(auth.user());
const store = useStore();

const {
  campaign,
  loading,
  submitting,
  isPlayerTier,
  tierLabel,
  tier,
  fetchCampaign,
  startTrial,
  getStartTrialErrorMessage,
} = useTrialCampaign();

const pageTitle = computed(() => (campaign.value?.name
  ? t('upgrade.claim_trial.page_title_campaign', { name: campaign.value?.name })
  : t('upgrade.claim_trial.page_title')));

const pageEmbedImage = computed(
  () => campaign.value?.image_url
    || 'https://refrag.gg/cdn-cgi/imagedelivery/5wML_ikJr-qv52ESeLE6CQ/wordpress.refrag.gg/2026/03/refrag_bg.png/public',
);

useHead({
  title: () => pageTitle.value,
  meta: [
    {
      name: 'description',
      content: () => t('upgrade.claim_trial.meta_description'),
    },
    {
      name: 'og:title',
      content: () => pageTitle.value,
    },
    {
      name: 'twitter:card',
      content: 'summary_large_image',
    },
    {
      name: 'twitter:domain',
      content: 'play.refrag.gg',
    },
    {
      name: 'og:image',
      content: () => pageEmbedImage.value,
    },
    {
      name: 'twitter:image',
      content: () => pageEmbedImage.value,
    },
  ],
});

/** Campaign name from link: /claim_trial/:source */
function getCampaignNameFromSource() {
  const fromQuery = route.params.source;
  if (fromQuery && typeof fromQuery === 'string') return fromQuery.trim();
  return null;
}

onMounted(async () => {
  const campaignName = getCampaignNameFromSource();
  if (!campaignName) {
    loading.value = false;
    router.replace('/');
    return;
  }
  try {
    await fetchCampaign(campaignName);
  } catch (err) {
    if (err.response?.status === 404) {
      store.commit('showMessage', {
        messageType: 'error',
        messageText: t('upgrade.claim_trial.offer_unavailable'),
      });
    }
    router.replace('/');
  } finally {
    loading.value = false;
  }
});

async function continueAndSubmit() {
  const campaignName = getCampaignNameFromSource();
  if (!campaignName || !campaign.value) return;
  submitting.value = true;
  try {
    await startTrial(campaignName);
    store.commit('showMessage', {
      messageType: 'success',
      messageText: t('upgrade.claim_trial.trial_started'),
    });
    gtm.push({ event: 'trial_started' });
    await auth.fetch();
    user.value = auth.user();
    setTimeout(() => router.push('/'), 1500);
  } catch (err) {
    store.commit('showMessage', {
      messageType: 'error',
      messageText: getStartTrialErrorMessage(err),
    });
  } finally {
    submitting.value = false;
  }
}
</script>
